import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Wallet as WalletIcon, Check, ShieldCheck, X } from "lucide-react";
import { trackEvent } from "../utils/metaPixel";

const API = "https://bhavanaastro.onrender.com/api";
const QUICK_AMOUNTS = [50, 100, 200, 500, 1000, 2000];
const SUPPORT_EMAIL = "contact.astroseven@gmail.com";

const walletCss = `
  .as-wallet { font-family: 'Hind', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }
  .as-display { font-family: 'Baloo 2', 'Hind', system-ui, sans-serif; }
  .as-sky {
    background:
      radial-gradient(1.5px 1.5px at 12% 20%, rgba(255,255,255,.7), transparent 60%),
      radial-gradient(1px 1px at 78% 14%, rgba(255,255,255,.6), transparent 60%),
      radial-gradient(1.5px 1.5px at 90% 60%, rgba(255,255,255,.5), transparent 60%),
      radial-gradient(1px 1px at 34% 78%, rgba(255,255,255,.5), transparent 60%),
      radial-gradient(120% 70% at 90% 0%, rgba(255,182,39,.20), transparent 60%),
      #17143F;
  }
`;

const cardShadow =
  "shadow-[0_1px_2px_rgba(23,20,63,0.06),0_10px_24px_-14px_rgba(23,20,63,0.25)]";

// Load Razorpay script dynamically
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById("razorpay-script")) return resolve(true);
    const script = document.createElement("script");
    script.id = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export default function Wallet() {
  const [searchParams] = useSearchParams();

  const [amount, setAmount] = useState(() => {
    const preset = parseInt(searchParams.get("amount"), 10);
    return preset > 0 ? String(preset) : "";
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [balance, setBalance] = useState(null); // null = still loading
  const [minRate, setMinRate] = useState(null); // cheapest AI chat rate, null = unknown

  const [showResultPopup, setShowResultPopup] = useState(false);
  const [resultType, setResultType] = useState(null); // "success" | "failed"
  const [paidAmount, setPaidAmount] = useState(0);

  const suggestedRef = useRef(false);

  const storedUser = sessionStorage.getItem("user") || localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const userId = user?._id || user?.id;
  const { name, email, mobile } = user || {};

  const amountNum = parseInt(amount, 10) || 0;

  // ---------- data ----------
  const fetchBalance = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API}/users/${userId}/details`);
      setBalance(res.data.wallet?.balance || 0);
    } catch (err) {
      console.error("Failed to fetch balance:", err);
    }
  }, [userId]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Cheapest AI chat rate, so we can show what a recharge buys
  useEffect(() => {
    axios
      .get(`${API}/Consult-astrologers?isAI=true&limit=100`)
      .then((res) => {
        const rates = (res.data.astrologers || [])
          .filter((a) => a.isAI === true || a.isAI === "true")
          .map((a) => Number(a.rates?.chat))
          .filter((r) => r > 0);
        setMinRate(rates.length ? Math.min(...rates) : null);
      })
      .catch((err) => console.error("Failed to fetch rates:", err));
  }, []);

  // One time: if the balance is below what a chat needs, pre-pick an amount that covers it
  useEffect(() => {
    if (suggestedRef.current || balance === null || minRate === null) return;
    suggestedRef.current = true;
    if (amount) return;
    const short = minRate * 5 - balance;
    if (short > 0) {
      const pick = QUICK_AMOUNTS.find((a) => a >= short);
      if (pick) setAmount(String(pick));
    }
  }, [balance, minRate, amount]);

  // ---------- payment ----------
  const openRazorpayCheckout = useCallback(
    ({ razorpayOrderId, keyId, amount: rzpAmount, currency, internalOrderId }) => {
      const options = {
        key: keyId,
        amount: rzpAmount, // already in paise from backend
        currency: currency || "INR",
        name: "Astro Seven",
        description: "Wallet recharge",
        order_id: razorpayOrderId,
        prefill: {
          name: name || "",
          email: email || "",
          contact: mobile || "",
        },
        theme: { color: "#3A2E9C" },
        handler: async function (response) {
          // Payment success: verify signature on backend
          try {
            setLoading(true);
            const res = await axios.post(`${API}/wallet/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId: internalOrderId,
            });

            const status = (res.data.orderStatus || "").toLowerCase();
            const isPaid = status === "paid" || status === "captured";
            const credited = Number(res.data.transaction?.amount) || amountNum;

            if (isPaid) {
              // Meta Pixel: Purchase (wallet top-up), once per order
              const trackedKey = `fbq_purchase_${internalOrderId}`;
              if (!sessionStorage.getItem(trackedKey)) {
                trackEvent("Purchase", {
                  content_name: "Wallet Top-up",
                  content_type: "product",
                  value: credited,
                  currency: "INR",
                });
                sessionStorage.setItem(trackedKey, "1");
              }
              setPaidAmount(credited);
              setAmount("");
            }

            setResultType(isPaid ? "success" : "failed");
            setShowResultPopup(true);
            await fetchBalance();
          } catch (err) {
            console.error(err);
            setError("Payment ho gaya lekin verify nahi ho paaya. Support se baat karo.");
            setResultType("failed");
            setShowResultPopup(true);
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setError("Payment cancel ho gaya.");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        setError(`Payment fail ho gaya: ${response.error.description}`);
        setResultType("failed");
        setShowResultPopup(true);
        setLoading(false);
      });
      rzp.open();
    },
    [name, email, mobile, amountNum, fetchBalance]
  );

  const handleRecharge = async () => {
    if (!amountNum) {
      setError("Amount chuno ya likho.");
      return;
    }
    if (!userId) {
      setError("Pehle login karo.");
      return;
    }
    setError("");

    try {
      setLoading(true);

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setError("Razorpay load nahi hua. Internet check karke dobara try karo.");
        setLoading(false);
        return;
      }

      const res = await axios.post(`${API}/wallet/topup`, {
        userId,
        amount: amountNum,
        phone: mobile,
        name,
        email,
      });

      const {
        orderId: internalOrderId,
        razorpayOrderId,
        keyId,
        amount: rzpAmount,
        currency,
      } = res.data;
      if (!razorpayOrderId) {
        setError("Server se payment order nahi mila. Dobara try karo.");
        setLoading(false);
        return;
      }

      trackEvent("InitiateCheckout", {
        content_name: "Wallet Top-up",
        value: amountNum,
        currency: "INR",
      });

      openRazorpayCheckout({
        razorpayOrderId,
        keyId,
        amount: rzpAmount,
        currency,
        internalOrderId,
      });
    } catch (err) {
      console.error("Failed to create wallet recharge:", err);
      setError("Payment shuru nahi ho paaya. Dobara try karo.");
      setLoading(false);
    }
  };

  // ---------- derived ----------
  const needForChat = minRate ? minRate * 5 : null;
  const hasEnough = balance !== null && needForChat !== null && balance >= needForChat;
  const shortBy = balance !== null && needForChat !== null ? needForChat - balance : 0;

  if (!user) {
    return (
      <div className="as-wallet min-h-screen bg-[#F5F4FB] px-4 py-16 text-center">
        <style>{walletCss}</style>
        <p className="text-lg font-semibold text-[#17143F]">Wallet use karne ke liye login karo.</p>
        <Link
          to="/login?next=/user/wallet"
          className="as-display mt-4 inline-block rounded-2xl bg-[#FFB627] px-8 py-3 text-[18px] font-bold text-[#1E1A4D]"
        >
          Login karo
        </Link>
      </div>
    );
  }

  return (
    <div className="as-wallet min-h-screen bg-[#F5F4FB] px-4 pb-12 pt-6 text-slate-800">
      <style>{walletCss}</style>

      <div className="mx-auto w-full max-w-lg">
        {/* ---------- Balance ---------- */}
        <section className="as-sky rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-indigo-200">Wallet balance</p>
              <p className="as-display mt-1 text-[40px] font-extrabold leading-none">
                {balance === null ? "..." : `₹${balance}`}
              </p>
            </div>
            <WalletIcon size={32} className="text-[#FFB627]" />
          </div>

          {balance !== null && needForChat !== null && (
            <p className="mt-4 rounded-xl bg-white/10 px-3.5 py-2.5 text-[14px] leading-snug text-indigo-100">
              {hasEnough
                ? "Aapke balance se AI chat shuru ho sakti hai."
                : `AI chat shuru karne ke liye kam se kam ₹${needForChat} chahiye (₹${minRate}/min, 5 minute). ₹${shortBy} aur add karo.`}
            </p>
          )}
        </section>

        {/* ---------- Add money ---------- */}
        <section className={`mt-5 rounded-2xl bg-white p-5 ${cardShadow}`}>
          <h1 className="as-display text-[22px] font-bold leading-tight text-[#17143F]">
            Kitne paise add karne hain?
          </h1>

          <div className="mt-4 grid grid-cols-3 gap-2.5">
            {QUICK_AMOUNTS.map((amt) => {
              const selected = amountNum === amt;
              return (
                <button
                  key={amt}
                  type="button"
                  onClick={() => {
                    setAmount(String(amt));
                    setError("");
                  }}
                  className={`relative rounded-xl border px-2 py-3 text-center transition ${
                    selected
                      ? "border-[#3A2E9C] bg-[#EEEBFA]"
                      : "border-[#DAD6F0] bg-white hover:border-[#3A2E9C]"
                  }`}
                >
                  {selected && (
                    <span className="absolute right-1.5 top-1.5 grid h-4 w-4 place-items-center rounded-full bg-[#3A2E9C] text-white">
                      <Check size={11} strokeWidth={3} />
                    </span>
                  )}
                  <span className="as-display block text-[20px] font-bold leading-none text-[#17143F]">
                    ₹{amt}
                  </span>
                  {minRate && (
                    <span className="mt-1 block text-[12px] text-slate-500">
                      ~{Math.floor(amt / minRate)} min chat
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <label htmlFor="amount" className="mt-5 block text-sm font-semibold text-[#17143F]">
            Ya apna amount likho
          </label>
          <div className="relative mt-1.5">
            <span className="absolute inset-y-0 left-4 grid place-items-center text-[16px] text-slate-500">
              ₹
            </span>
            <input
              id="amount"
              type="text"
              inputMode="numeric"
              placeholder="Amount"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value.replace(/\D/g, "").slice(0, 6));
                setError("");
              }}
              className="w-full rounded-xl border border-[#DAD6F0] bg-white py-3 pl-9 pr-4 text-[16px] text-[#17143F] placeholder:text-slate-400 focus:border-[#3A2E9C] focus:outline-none focus:ring-2 focus:ring-[#3A2E9C]/20"
            />
          </div>
          {minRate && (
            <p className="mt-1.5 text-xs text-slate-500">
              Minute ka andaza sabse sasti AI chat (₹{minRate}/min) ke hisaab se hai. Har astrologer ka rate alag ho sakta hai.
            </p>
          )}

          {error && !showResultPopup && (
            <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={handleRecharge}
            disabled={loading || !amountNum}
            className="as-display mt-5 flex w-full items-center justify-center rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[19px] font-bold text-[#1E1A4D] shadow-[0_10px_28px_-10px_rgba(255,182,39,0.7)] transition hover:bg-[#FFC24D] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Processing..." : amountNum ? `₹${amountNum} add karo` : "Amount chuno"}
          </button>

          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck size={15} className="text-[#3A2E9C]" />
            Razorpay se secure payment. Payment ke baad balance yahin update ho jata hai.
          </p>
        </section>
      </div>

      {/* ---------- Payment result ---------- */}
      {showResultPopup && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center">
          <div
            className="w-full max-w-md rounded-t-3xl bg-white p-6 text-center sm:rounded-3xl"
            style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
          >
            {resultType === "success" ? (
              <>
                <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-green-100 text-green-600">
                  <Check size={30} strokeWidth={3} />
                </div>
                <h2 className="as-display text-[24px] font-bold text-[#17143F]">Payment ho gaya</h2>
                <p className="mt-1 text-[15px] text-slate-600">
                  ₹{paidAmount} aapke wallet mein add ho gaye.
                </p>
                <p className="mt-0.5 text-sm text-slate-500">Naya balance: ₹{balance}</p>

          <Link
  to={sessionStorage.getItem("asReturnToChat") || "/user/dashboard"}
  onClick={() => sessionStorage.removeItem("asReturnToChat")}
  className="as-display mt-5 flex w-full items-center justify-center rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[18px] font-bold text-[#1E1A4D]"
>
  {sessionStorage.getItem("asReturnToChat") ? "Chat par wapas jao" : "Astrologer chuno"}
</Link>
              </>
            ) : (
              <>
                <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-red-100 text-red-600">
                  <X size={30} strokeWidth={3} />
                </div>
                <h2 className="as-display text-[24px] font-bold text-[#17143F]">Payment nahi hua</h2>
                <p className="mt-1 text-[15px] leading-snug text-slate-600">
                  Dobara try karo. Agar paise kat gaye hain, to pehle balance check karo. Wallet mein na dikhe to{" "}
                  <a className="font-semibold text-[#3A2E9C] underline" href={`mailto:${SUPPORT_EMAIL}`}>
                    {SUPPORT_EMAIL}
                  </a>{" "}
                  par likho.
                </p>
              </>
            )}

            <button
              type="button"
              onClick={() => {
                setShowResultPopup(false);
                setError("");
              }}
              className="mt-3 block w-full py-2 text-center text-sm font-medium text-[#3A2E9C]"
            >
              {resultType === "success" ? "Theek hai" : "Band karo"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}